import apiClient from './client'
import { buildJsonApiQuery } from '@/utils/jsonapi'

/**
 * Folders API module
 * Handles CRUD operations and reordering for folders
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */
const foldersAPI = {
  /**
   * Get all folders with optional filtering
   * @param {Object} [queryOptions] - JSON:API query options
   * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { parent: null })
   * @param {Array<string>|string} [queryOptions.sort] - Sort fields
   * @param {Array<string>|string} [queryOptions.include] - Relations to include
   * @returns {Promise<Object>} JSON:API response with data array
   */
  getAll: async (queryOptions = {}) => {
    const params = buildJsonApiQuery(queryOptions)
    const response = await apiClient.get('/folders/', { params })
    return response.data
  },

  /**
   * Get a single folder by ID
   * @param {string} folderId - Folder UUID
   * @param {Object} [queryOptions] - JSON:API query options
   * @returns {Promise<Object>} JSON:API response with folder data
   */
  getById: async (folderId, queryOptions = {}) => {
    const params = buildJsonApiQuery(queryOptions)
    const response = await apiClient.get(`/folders/${folderId}/`, { params })
    return response.data
  },

  /**
   * Create a new folder
   * @param {Object} data - Folder data
   * @param {string} data.name - Folder name
   * @param {string} [data.description] - Folder description
   * @param {string|null} [data.parent] - Parent folder ID or null for root
   * @returns {Promise<Object>} JSON:API response with created folder
   */
  create: async (data) => {
    const response = await apiClient.post('/folders/', data)
    return response.data
  },

  /**
   * Update an existing folder
   * @param {string} folderId - Folder UUID
   * @param {Object} data - Updated folder data
   * @returns {Promise<Object>} JSON:API response with updated folder
   */
  update: async (folderId, data) => {
    const response = await apiClient.patch(`/folders/${folderId}/`, data)
    return response.data
  },

  /**
   * Delete a folder
   * @param {string} folderId - Folder UUID
   * @returns {Promise<void>}
   */
  delete: async (folderId) => {
    await apiClient.delete(`/folders/${folderId}/`)
  },

  /**
   * Bulk update sort_order for items inside a folder
   * @param {string} folderId - Parent folder UUID
   * @param {Object} items - Items to reorder
   * @param {Array} [items.folders] - Folder reorder items [{id, sort_order}]
   * @param {Array} [items.dashboards] - Dashboard reorder items [{id, sort_order}]
   * @param {Array} [items.metrics] - Metric reorder items [{id, sort_order}]
   * @returns {Promise<Object>} Response with updated count
   */
  reorder: async (folderId, items) => {
    const response = await apiClient.post(`/folders/${folderId}/reorder/`, items)
    return response.data
  },

  /**
   * Bulk update sort_order for root-level items (no parent folder)
   * @param {Object} items - Items to reorder
   * @param {Array} [items.folders] - Folder reorder items [{id, sort_order}]
   * @param {Array} [items.dashboards] - Dashboard reorder items [{id, sort_order}]
   * @param {Array} [items.metrics] - Metric reorder items [{id, sort_order}]
   * @returns {Promise<Object>} Response with updated count
   */
  reorderRoot: async (items) => {
    const response = await apiClient.post('/folders/reorder-root/', items)
    return response.data
  },
}

export default foldersAPI
